/**
 * Dahua NVR / XVR deep-dive catalog — extension to dahuaCatalog.ts.
 *
 * Every entry below was verified on 2026-05-14 against a primary Dahua
 * datasheet PDF fetched from material.dahuasecurity.com (or, for a small
 * number of older SKUs, from dahuasecurity.com /asset/upload/...).
 * Source URL appears as the `datasheetUrl` on each entry; fields are
 * left undefined when the datasheet does not list a value — never guessed.
 *
 * Coverage:
 *   - Lite 4000-4KS3 NVRs (no-AI compact / 1U / mini-tower)
 *   - WizSense 4000-EI NVRs (AI-on-recorder, 4-EI generation)
 *   - WizSense 5000-EI NVRs (AcuPick, 2-HDD / 4-HDD / 8-HDD)
 *   - WizMind 600-XI enterprise NVRs (8-HDD, RAID, 2.5 GbE)
 *   - XVR penta-brid (HDCVI/AHD/TVI/CVBS/IP hybrid) DVRs:
 *       Cooper (XVR1A/1B), Lite (XVR4xxxHS, XVR5xxxHS),
 *       Pro (XVR7xxxHE-4KL-X), AI (XVR8xxxA-4K-I)
 *
 * Excluded (already present in dahuaCatalog.ts): NVR4108HS-8P-4KS3,
 * NVR4216-16P-4KS3, NVR4232-4KS3, NVR4216-EI, NVR4216-16P-EI,
 * NVR4416-EI, NVR5216-16P-EI, NVR5432-16P-EI, NVR608H-64-XI,
 * NVR608RH-128-XI, XVR5108HS-4KL-I3, XVR7216A-4KL-X.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DAHUA_NVR_DEEP_DIVE: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // Lite 4000-4KS3 — no on-board AI (SMD Plus only; AI requires camera-side)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4104hs-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4104HS-4KS3',
    category: 'nvr-recorder',
    tagline: '4-ch compact Lite NVR — 1 SATA (20 TB), 80 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4108hs-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite NVR — 1 SATA (20 TB), 80 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4116hs-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch compact Lite NVR — 1 SATA (20 TB), 160 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4108-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch Smart 1U Lite NVR — 1 SATA (20 TB), 80 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4116-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4116-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch Smart 1U Lite NVR — 1 SATA (20 TB), 160 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-8P-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4108-8p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108-8P-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch Lite 1U NVR — 8-port PoE (72 W), 1 SATA (20 TB), 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-8P-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104-4KS3_S0_datasheet_20231027.pdf
  // (PDF documents DH-NVR4104-P-4KS3 — i.e. with 4-port PoE — under the
  // shared NVR4104-4KS3 filename.)
  {
    id: 'dahua-nvr4104-p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4104-P-4KS3',
    category: 'nvr-recorder',
    tagline: '4-ch Lite 1U NVR — 4-port PoE (36 W), 1 SATA (20 TB), 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 4,
      poeBudgetW: 36,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4204-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4204-4KS3',
    category: 'nvr-recorder',
    tagline: '4-ch Lite 1U NVR — 2 SATA (20 TB each), 160 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4208-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4208-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch Lite 1U NVR — 2 SATA (20 TB each), 160 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-4KS3_S0_datasheet_20231027.pdf
  {
    id: 'dahua-nvr4216-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4216-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch Lite 1U NVR — 2 SATA (20 TB each), 160 Mbps in, 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizSense 4000-EI — AI chip on board (face / perimeter / SMD by NVR)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-8P-EI_S0_datasheet_20230628.pdf
  {
    id: 'dahua-nvr4108-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108-8P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense Smart NVR — 8-port PoE (72 W), 1 SATA (16 TB), AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-8P-EI_S0_datasheet_20230628.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '32 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4108hs-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-8P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense compact NVR — 8-port PoE (72 W), 1 SATA (16 TB), AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '32 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-EI_S0_datasheet_20230214.pdf
  {
    id: 'dahua-nvr4108-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense Smart 1U NVR — 1 SATA (16 TB), no built-in PoE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108-EI_S0_datasheet_20230214.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '32 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116-EI_S0_datasheet_20230214.pdf
  {
    id: 'dahua-nvr4116-ei',
    manufacturer: 'Dahua',
    model: 'NVR4116-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense Smart 1U NVR — 1 SATA (16 TB), no built-in PoE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116-EI_S0_datasheet_20230214.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '32 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-EI_S0_datasheet_20230214.pdf
  {
    id: 'dahua-nvr4108hs-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense compact NVR — 1 SATA (16 TB), 80 Mbps, no PoE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-EI_S0_datasheet_20230214.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '32 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-EI_S0_datasheet-20230214.pdf
  {
    id: 'dahua-nvr4116hs-ei',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense compact NVR — 1 SATA (16 TB), 256 Mbps, no PoE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-EI_S0_datasheet-20230214.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '32 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4232-ei',
    manufacturer: 'Dahua',
    model: 'NVR4232-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1U NVR — 2 SATA (16 TB each), no built-in PoE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4432-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4432-ei',
    manufacturer: 'Dahua',
    model: 'NVR4432-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 4 SATA (16 TB each), dual GbE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4432-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizSense 5000-EI — AcuPick, 384 Mbps, 2 / 4 / 8 HDD bays
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5208-8P-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5208-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR5208-8P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense 1U NVR — 8-port PoE (130 W), 2 SATA (20 TB), AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5208-8P-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5216-ei',
    manufacturer: 'Dahua',
    model: 'NVR5216-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1U NVR — 2 SATA (20 TB each), AcuPick, no built-in PoE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5232-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5232-ei',
    manufacturer: 'Dahua',
    model: 'NVR5232-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1U NVR — 2 SATA (20 TB each), AcuPick, no built-in PoE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5232-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5464-EI_S0_datasheet_20231124.pdf
  {
    id: 'dahua-nvr5464-ei',
    manufacturer: 'Dahua',
    model: 'NVR5464-EI',
    category: 'nvr-recorder',
    tagline: '64-ch WizSense 1.5U NVR — 4 SATA (20 TB each), dual GbE, AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5464-EI_S0_datasheet_20231124.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5864-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5864-ei',
    manufacturer: 'Dahua',
    model: 'NVR5864-EI',
    category: 'nvr-recorder',
    tagline: '64-ch WizSense 2U NVR — 8 SATA (20 TB each), RAID 0/1/5/6/10, AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5864-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-EI_S0_datasheet_20230710.pdf
  {
    id: 'dahua-nvr5432-ei',
    manufacturer: 'Dahua',
    model: 'NVR5432-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 4 SATA (16 TB each), dual GbE, AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-EI_S0_datasheet_20230710.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816-EI_S0_datasheet_20240625.pdf
  {
    id: 'dahua-nvr5816-ei',
    manufacturer: 'Dahua',
    model: 'NVR5816-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 2U NVR — 8 SATA (20 TB each), RAID 0/1/5/6/10, AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5816-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832-EI_S0_datasheet_20230710.pdf
  {
    id: 'dahua-nvr5832-ei',
    manufacturer: 'Dahua',
    model: 'NVR5832-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 2U NVR — 8 SATA (16 TB each), RAID 0/1/5/6/10, AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5832-EI_S0_datasheet_20230710.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // WizMind 600-XI — enterprise (8-HDD, RAID, 2.5 GbE, ANPR, 1024 Mbps)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-32-XI_S0_datasheet_20230322.pdf
  {
    id: 'dahua-nvr608h-32-xi',
    manufacturer: 'Dahua',
    model: 'NVR608H-32-XI',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — 8 SATA (16 TB), RAID 0/1/5/6/10, 2× 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-32-XI_S0_datasheet_20230322.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 1024,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-128-XI_S0_datasheet_20230322.pdf
  {
    id: 'dahua-nvr608h-128-xi',
    manufacturer: 'Dahua',
    model: 'NVR608H-128-XI',
    category: 'nvr-recorder',
    tagline: '128-ch WizMind 2U NVR — 8 SATA (16 TB), RAID 0/1/5/6/10, 2× 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-128-XI_S0_datasheet_20230322.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 1024,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-32-XI_S0_datasheet_20230322.pdf
  {
    id: 'dahua-nvr608rh-32-xi',
    manufacturer: 'Dahua',
    model: 'NVR608RH-32-XI',
    category: 'nvr-recorder',
    tagline: '32-ch WizMind 2U NVR — redundant PSU, 8 SATA, RAID 0/1/5/6/10, 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-32-XI_S0_datasheet_20230322.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 1024,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-64-XI_S0_datasheet_20231021.pdf
  {
    id: 'dahua-nvr608rh-64-xi',
    manufacturer: 'Dahua',
    model: 'NVR608RH-64-XI',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 2U NVR — redundant PSU, 8 SATA, RAID 0/1/5/6/10, AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-64-XI_S0_datasheet_20231021.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // XVR penta-brid DVRs — Cooper / Lite / Pro / AI
  // (hybrid = true; accept HDCVI / AHD / TVI / CVBS analog + IP cameras)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR1A04-08_datasheet_20201027.pdf
  {
    id: 'dahua-xvr1a04',
    manufacturer: 'Dahua',
    model: 'XVR1A04',
    category: 'nvr-recorder',
    tagline: '4-ch Cooper penta-brid DVR — 1080N/720p analog, 1 SATA (6 TB), +1 IP @ 2 MP',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR1A04-08_datasheet_20201027.pdf',
    specs: {
      channels: 5, // 4 BNC + 1 IP
      inputBandwidthMbps: 20,
      maxResolution: '2 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR1A04-08_datasheet_20201027.pdf
  {
    id: 'dahua-xvr1a08',
    manufacturer: 'Dahua',
    model: 'XVR1A08',
    category: 'nvr-recorder',
    tagline: '8-ch Cooper penta-brid DVR — 1080N/720p analog, 1 SATA (6 TB), +2 IP @ 5 MP',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR1A04-08_datasheet_20201027.pdf',
    specs: {
      channels: 10, // 8 BNC + 2 IP
      inputBandwidthMbps: 40,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B16-I_datasheet_20220530.pdf
  // (Cooper-I 16-ch is XVR1B16-I; there is no XVR1A16 SKU.)
  {
    id: 'dahua-xvr1b16-i',
    manufacturer: 'Dahua',
    model: 'XVR1B16-I',
    category: 'nvr-recorder',
    tagline: '16-ch Cooper-I penta-brid DVR — 1080N/720p analog, 1 SATA (16 TB), +18 IP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B16-I_datasheet_20220530.pdf',
    specs: {
      channels: 18, // 16 BNC + 2 IP by default, expandable to 18 IP
      inputBandwidthMbps: 64,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4104HS-I_datasheet_20220905.pdf
  {
    id: 'dahua-xvr4104hs-i',
    manufacturer: 'Dahua',
    model: 'XVR4104HS-I',
    category: 'nvr-recorder',
    tagline: '4-ch Lite penta-brid WizSense DVR — 1080N/720p, 1 SATA, +1 IP @ 2 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4104HS-I_datasheet_20220905.pdf',
    specs: {
      channels: 5,
      inputBandwidthMbps: 32,
      maxResolution: '2 MP',
      hddBays: 1,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4108HS-I_datasheet_20220530.pdf
  {
    id: 'dahua-xvr4108hs-i',
    manufacturer: 'Dahua',
    model: 'XVR4108HS-I',
    category: 'nvr-recorder',
    tagline: '8-ch Lite penta-brid WizSense DVR — 1080N/720p, 1 SATA, +2 IP @ 6 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4108HS-I_datasheet_20220530.pdf',
    specs: {
      channels: 10,
      inputBandwidthMbps: 32,
      maxResolution: '6 MP',
      hddBays: 1,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4116HS-I_datasheet_20220530.pdf
  {
    id: 'dahua-xvr4116hs-i',
    manufacturer: 'Dahua',
    model: 'XVR4116HS-I',
    category: 'nvr-recorder',
    tagline: '16-ch Lite penta-brid WizSense DVR — 720p analog, 1 SATA, +2 IP @ 6 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4116HS-I_datasheet_20220530.pdf',
    specs: {
      channels: 18,
      inputBandwidthMbps: 64,
      maxResolution: '6 MP',
      hddBays: 1,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://www.dahuasecurity.com/asset/upload/product/20180821/XVR4104HS-X1_Datasheet_20180731.pdf
  {
    id: 'dahua-xvr4104hs-x1',
    manufacturer: 'Dahua',
    model: 'XVR4104HS-X1',
    category: 'nvr-recorder',
    tagline: '4-ch Lite penta-brid DVR — 720p analog, 1 SATA (6 TB), +1 IP @ 2 MP',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/product/20180821/XVR4104HS-X1_Datasheet_20180731.pdf',
    specs: {
      channels: 5,
      inputBandwidthMbps: 20,
      maxResolution: '2 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
    },
  },

  // https://www.dahuasecurity.com/asset/upload/product/20180308/DH-XVR4116HS-X_Datasheet_201803061.pdf
  {
    id: 'dahua-xvr4116hs-x',
    manufacturer: 'Dahua',
    model: 'XVR4116HS-X',
    category: 'nvr-recorder',
    tagline: '16-ch Lite penta-brid DVR — 720p analog, 1 SATA (10 TB), +2 IP @ 6 MP',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/product/20180308/DH-XVR4116HS-X_Datasheet_201803061.pdf',
    specs: {
      channels: 18,
      inputBandwidthMbps: 72,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104HS-4KL-I3_V3_datasheet_20230720.pdf
  {
    id: 'dahua-xvr5104hs-4kl-i3',
    manufacturer: 'Dahua',
    model: 'XVR5104HS-4KL-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Lite penta-brid 4K-N/5MP WizSense DVR — 1 SATA (16 TB), +4 IP @ 8 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104HS-4KL-I3_V3_datasheet_20230720.pdf',
    specs: {
      channels: 8, // 4 BNC + 4 IP
      inputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5116HS-I3_datasheet_20230720.pdf
  {
    id: 'dahua-xvr5116hs-i3',
    manufacturer: 'Dahua',
    model: 'XVR5116HS-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Lite penta-brid 5MP-Value WizSense DVR — 1 SATA (16 TB), +24 IP @ 6 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5116HS-I3_datasheet_20230720.pdf',
    specs: {
      channels: 24,
      inputBandwidthMbps: 128,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://www.securityinformed.com/datasheets/dahua-technology-dh-xvr5108h-4kl-x-8p-digital-video-recorder-dvr/co-14772-ga/XVR5108H-4KL-X-8P_Datasheet_20180704.pdf
  {
    id: 'dahua-xvr5108h-4kl-x-8p',
    manufacturer: 'Dahua',
    model: 'XVR5108H-4KL-X-8P',
    category: 'nvr-recorder',
    tagline: '8-ch Lite penta-brid 4K DVR — 8 PoC analog, 1 SATA (10 TB), +4 IP @ 8 MP',
    verified: true,
    datasheetUrl: 'https://www.securityinformed.com/datasheets/dahua-technology-dh-xvr5108h-4kl-x-8p-digital-video-recorder-dvr/co-14772-ga/XVR5108H-4KL-X-8P_Datasheet_20180704.pdf',
    specs: {
      channels: 12, // 8 BNC + 4 IP
      inputBandwidthMbps: 48,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5208AN-4KL-I3_V2_datasheet_20240723.pdf
  {
    id: 'dahua-xvr5208an-4kl-i3',
    manufacturer: 'Dahua',
    model: 'XVR5208AN-4KL-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Lite penta-brid 4K-Value/5MP WizSense DVR — 2 SATA (16 TB), +16 IP @ 8 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5208AN-4KL-I3_V2_datasheet_20240723.pdf',
    specs: {
      channels: 24, // 8 BNC + 16 IP
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5216AN-4KL-I3_datasheet_20230107.pdf
  {
    id: 'dahua-xvr5216an-4kl-i3',
    manufacturer: 'Dahua',
    model: 'XVR5216AN-4KL-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Lite penta-brid 4K-N/5MP WizSense DVR — 2 SATA (16 TB), +32 IP @ 8 MP',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5216AN-4KL-I3_datasheet_20230107.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 128,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7108-16HE-4KL-X_datasheet_20201027.pdf
  {
    id: 'dahua-xvr7108he-4kl-x',
    manufacturer: 'Dahua',
    model: 'XVR7108HE-4KL-X',
    category: 'nvr-recorder',
    tagline: '8-ch Pro penta-brid 4K Mini DVR — 1 SATA (10 TB), +16 IP @ 8 MP',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7108-16HE-4KL-X_datasheet_20201027.pdf',
    specs: {
      channels: 24, // 8 BNC + 16 IP
      inputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7108-16HE-4KL-X_datasheet_20201027.pdf
  {
    id: 'dahua-xvr7116he-4kl-x',
    manufacturer: 'Dahua',
    model: 'XVR7116HE-4KL-X',
    category: 'nvr-recorder',
    tagline: '16-ch Pro penta-brid 4K Mini DVR — 1 SATA (10 TB), +32 IP @ 8 MP',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7108-16HE-4KL-X_datasheet_20201027.pdf',
    specs: {
      channels: 48, // 16 BNC + 32 IP
      inputBandwidthMbps: 128,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://www.securityinformed.com/datasheets/dahua-technology-xvr8208a-4k-i-digital-video-recorder-dvr/co-14772-ga/XVR8208A-4K-I.pdf
  // (XVR8000-4K-I "AI" series; same shape as XVR8208A-4KL-I but 12 MP IP.)
  {
    id: 'dahua-xvr8208a-4k-i',
    manufacturer: 'Dahua',
    model: 'XVR8208A-4K-I',
    category: 'nvr-recorder',
    tagline: '8-ch AI penta-brid 4K DVR — 2 SATA (10 TB), 256 Mbps, +64 IP @ 12 MP',
    verified: true,
    datasheetUrl: 'https://www.securityinformed.com/datasheets/dahua-technology-xvr8208a-4k-i-digital-video-recorder-dvr/co-14772-ga/XVR8208A-4K-I.pdf',
    specs: {
      channels: 72, // 8 BNC + 64 IP
      inputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '2× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },
];
